import {makeAutoObservable} from 'mobx';
import {DisplayTypeEnum, RenderV2_DisplayItem, RenderV2Object} from './render-v2-d';
import React from 'react';

type SpiritSide = {
    top: boolean;
    left: boolean;
    bottom: boolean;
    right: boolean;
    vertical: boolean;
};

export class FrameBuilderStore {
    public frame: RenderV2Object;
    public matSize = 0.3;
    public matBorderSize = 0.07;
    public DPI = 0;
    public outerSize = 0.3;
    public innerSize = 0.12;
    public doubleInnerSize = this.innerSize * 2;

    constructor(frame: RenderV2Object) {
        this.frame = frame;
        makeAutoObservable(this);
    }

    get maxCols() {
        return Math.max(...this.frame.displayMatrix.map(r => r.length));
    }

    get colWidths() {
        return Array.from({length: this.maxCols}).map((_, colIdx) => Math.max(...this.frame.displayMatrix.map(row => (row[colIdx] ? (row[colIdx].width !== -1 ? row[colIdx].width : 0) : 0))));
    }

    get rowHeights() {
        return this.frame.displayMatrix.map(row => Math.max(...row.map(item => (item.height !== -1 && item.displayType === DisplayTypeEnum.Doc ? item.height : 0))));
    }

    get contentWidth() {
        return this.colWidths.reduce((a, b) => a + b, this.frame.railLeft + this.frame.railRight + this.getGap('gapRight', 'sum'));
    }

    get contentHeight() {
        return this.rowHeights.reduce((a, b) => a + b, this.frame.railTop + this.frame.railBottom + this.getGap('gapBelow', 'sum'));
    }

    get frameWidth() {
        return this.contentWidth + this.frame.moulding.mouldingThickness * 2;
    }

    get frameHeight() {
        return this.contentHeight + this.frame.moulding.mouldingThickness * 2;
    }

    get itemsByType(): Map<DisplayTypeEnum, RenderV2_DisplayItem[]> {
        const map = new Map<DisplayTypeEnum, RenderV2_DisplayItem[]>();
        this.frame.displayMatrix
            .flatMap(row => row)
            .forEach(item => {
                if (!item) return;
                const arr = map.get(item.displayType) || [];
                arr.push(item);
                map.set(item.displayType, arr);
            });
        return map;
    }

    get docRows() {
        return this.frame.displayMatrix.map(row => row.filter(item => item.displayType === DisplayTypeEnum.Doc)).filter(row => row.length > 0);
    }

    get isVertical() {
        return !this.docRows.some(row => row.length > 1);
    }

    get hasSpirit() {
        return !!this.frame.spiritMode;
    }

    get outerSides(): SpiritSide[] {
        return [
            {
                top: true,
                left: true,
                bottom: false,
                right: false,
                vertical: false,
            },
            {
                top: true,
                right: true,
                left: false,
                bottom: false,
                vertical: false,
            },
            {
                bottom: true,
                left: true,
                top: false,
                right: false,
                vertical: false,
            },
            {
                bottom: true,
                right: true,
                top: false,
                left: false,
                vertical: false,
            },
            {
                left: true,
                vertical: true,
                top: false,
                bottom: false,
                right: false,
            },
            {
                right: true,
                vertical: true,
                top: false,
                left: false,
                bottom: false,
            },
        ];
    }

    get spiritSides() {
        if (!this.hasSpirit) return [];

        return this.outerSides.map(side => ({
            outer: this.outerSideStyle(side),
            inner: this.innerSideStyle(side),
        }));
    }

    outerSideStyle = (side: SpiritSide): React.CSSProperties => {
        const {railLeft, railRight, railTop, railBottom, spiritMode} = this.frame;
        const os = this.outerSize;
        const is = this.innerSize;
        const dis = this.doubleInnerSize;
        const styles: React.CSSProperties = {background: spiritMode?.outerColor};

        if (side.vertical) {
            styles.top = this.inchToPx(railTop / 2 - dis);
            styles.left = side.left ? this.inchToPx(railLeft / 2 - dis) : 'auto';
            styles.right = side.right ? this.inchToPx(railRight / 2 - dis) : 'auto';
            styles.height = `calc(100% - ${this.inchToPx(railTop / 2 + railBottom / 2 - os - is)})`;
            styles.width = this.inchToPx(os);
        } else {
            const width = this.inchToPx(side.left ? railLeft / 2 : railRight / 2);
            const topCut = spiritMode?.topCutWidth ?? 0;
            const bottomCut = spiritMode?.bottomCutWidth ?? 0;
            const cut = side.top ? topCut / 2 : bottomCut / 2;

            styles.top = side.top ? this.inchToPx(railTop / 2 - dis) : 'auto';
            styles.bottom = side.bottom ? this.inchToPx(railBottom / 2 - dis) : 'auto';
            styles.height = this.inchToPx(os);
            styles.width = cut ? `calc(50% - ${width} - ${this.inchToPx(cut)})` : '50%';

            if (side.left) {
                styles.left = this.inchToPx(railLeft / 2 - dis);
            } else {
                styles.right = this.inchToPx(railRight / 2 - dis);
            }
        }

        return styles;
    };

    innerSideStyle = (side: SpiritSide): React.CSSProperties => {
        const {spiritMode} = this.frame;
        const is = this.innerSize;
        const dis = this.doubleInnerSize;
        const styles: React.CSSProperties = {background: spiritMode?.innerColor, zIndex: 1};

        if (side.vertical) {
            styles.height = `calc(100% - ${this.inchToPx(dis)})`;
            styles.width = this.inchToPx(is);
        } else {
            styles.height = this.inchToPx(is);
            styles.marginLeft = side.left ? this.inchToPx(is) : 'auto';
            styles.marginRight = side.right ? this.inchToPx(is) : 'auto';
            styles.width = `calc(100% - ${this.inchToPx(dis)})`;
        }

        return styles;
    };

    itemsCount = (type: DisplayTypeEnum) => {
        return this.itemsByType.get(type)?.length || 0;
    };

    getGap = (prop: 'gapRight' | 'gapBelow', mode: 'first' | 'sum') => {
        const elements = this.frame.displayMatrix.flatMap(row => row).filter(el => el.displayType === DisplayTypeEnum.Doc && el[prop] !== -1);

        if (!elements.length) return 0;

        return mode === 'first' ? elements[0][prop] : elements.reduce((sum, el) => sum + el[prop], 0);
    };

    containerStyle = (item: RenderV2_DisplayItem) => {
        return {
            width: this.inchToPx(item.width),
            height: this.inchToPx(item.height),
        };
    };

    matImageElements = (item: RenderV2_DisplayItem) => {
        if (!item.mat2ImageURL) return [];

        const {cornerTopLeftURL, cornerTopRightURL, cornerBottomRightURL, cornerBottomLeftURL, sideLeftURL, sideRightURL, sideTopURL, sideBottomURL} = item.mat2ImageURL;
        const offset = this.inchToPx(this.matSize);
        const innerSize = `calc(100% - ${this.inchToPx(this.matSize * 2)})`;

        const elements: {id: string; src: string; style: React.CSSProperties}[] = [
            {id: 'mat2_top_left', src: cornerTopLeftURL, style: {top: 0, left: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_top_right', src: cornerTopRightURL, style: {top: 0, right: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_bottom_right', src: cornerBottomRightURL, style: {bottom: 0, right: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_bottom_left', src: cornerBottomLeftURL, style: {bottom: 0, left: 0, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_left', src: sideLeftURL, style: {top: offset, left: 0, height: innerSize, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_right', src: sideRightURL, style: {top: offset, right: 0, height: innerSize, width: this.inchToPx(this.matSize)}},
            {id: 'mat2_top', src: sideTopURL, style: {top: 0, left: offset, width: innerSize, height: this.inchToPx(this.matSize)}},
            {id: 'mat2_bottom', src: sideBottomURL, style: {bottom: 0, left: offset, width: innerSize, height: this.inchToPx(this.matSize)}},
        ];

        return elements;
    };

    matBoxElements = (item: RenderV2_DisplayItem) => {
        const m = this.matSize;
        const width = item.width;
        const height = item.height;
        const color = item.mat2Color;

        return [
            {cls: 'mat2-outer', w: width, h: height},
            {cls: 'mat2-width absolute-center', w: width - 0.2, h: height - 0.2, extra: {border: `${this.matBorderSize}in solid ${color}`}},
            {cls: 'mat2-inner absolute-center', w: width - m * 2 - this.matBorderSize, h: height - m * 2 - this.matBorderSize},
            // {cls: 'mat2-box', w: width - m * 2, h: height - m * 2, extra: {top: this.inchToPx(0.15), left: this.inchToPx(0.15)}},
        ];
    };

    documentStyle = (item: RenderV2_DisplayItem) => {
        const m = this.matSize;

        if (item.mat2ImageURL) {
            return {
                width: this.inchToPx(item.width - m * 2),
                height: this.inchToPx(item.height - m * 2),
                top: this.inchToPx(m),
                left: this.inchToPx(m),
                position: 'absolute' as const,
            };
        } else {
            return {
                width: this.inchToPx(item.width - m * 2 - this.matBorderSize),
                height: this.inchToPx(item.height - m * 2 - this.matBorderSize),
            };
        }
    };

    inchToPx = (val: number) => {
        return `${val !== -1 ? val * this.DPI : 0}px`;
    };
}
